import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, AUTH_STATES, CONNECTION_STATES } from '../../contexts/AuthContext';
import PropTypes from 'prop-types';
import { validateId } from '../../utils/validation';
import { userStorage } from '../../utils/userStorage';
import { GlobalLoadingIndicator } from '../../ui/components/common';

const ProtectedRoute = ({ children, requiredRole = 0 }) => {
  const {
    user,
    loading,
    authState,
    connectionState,
    isAuthenticated,
    isOnline,
    tokenRefreshInProgress
  } = useAuth();
  const location = useLocation();

  // Validate user ID when component mounts or user changes
  React.useEffect(() => {
    if (user?.id) {
      try {
        validateId(user.id);
      } catch (error) {
        console.error('Invalid user ID format:', error);
      }
    }
  }, [user?.id]);

  // Show loading indicator only during initial authentication check (auth state not yet known).
  // Token refresh mid-session is handled by the AuthenticationLoadingHandler overlay in App.jsx.
  if (loading) {
    return (
      <GlobalLoadingIndicator
        isVisible={true}
        message="Loading your account..."
        fullScreen={true}
      />
    );
  }

  // Only redirect to login if authentication has definitively failed (not in progress)
  if (!isAuthenticated && !(connectionState === CONNECTION_STATES.OFFLINE && user?.id)) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Then check for user data
  if (!user?.id) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Finally check role requirements
  if (requiredRole > 0 && (!user.role || user.role < requiredRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRole: PropTypes.number,
};

export default ProtectedRoute; 